import { ReactNode } from 'react';
import { Heading, Text, Button } from 'frosted-ui';
import { InfoOutline16 } from '@frosted-ui/icons';
import { Rise } from '../animation';
import { useCountUp } from '../../hooks/useCountUp';
import { useIsMobile } from '../../hooks/useIsMobile';
import { LIFT, formatDollars as formatCurrency } from './lift';

interface ReceiptsScreenProps {
  revenue: number;
  onNext: () => void;
  onBack: () => void;
}

interface ReceiptRowProps {
  title: string;
  subtitle: string;
  amount: number;
  startDelayMs: number;
  amountColor?: string;
  isLast?: boolean;
  rowDelay: number;
}

function ReceiptRow({
  title,
  subtitle,
  amount,
  startDelayMs,
  amountColor,
  isLast,
  rowDelay,
}: ReceiptRowProps) {
  const animated = useCountUp({ target: amount, durationMs: 950, startDelayMs });
  const isMobile = useIsMobile();

  return (
    <Rise delay={rowDelay}>
      <div
        className="vara2-receipt-row"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-4)',
          padding: 'var(--space-4) 0',
          borderBottom: isLast ? 'none' : '1px solid var(--gray-a5)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Text size="3" weight="medium">
            {title}
          </Text>
          <Text size="2" color="gray">
            {subtitle}
          </Text>
        </div>
        <Heading
          size={isMobile ? '6' : '7'}
          className="vara2-tabular"
          style={amountColor ? { color: amountColor } : undefined}
        >
          +{formatCurrency(animated)}
        </Heading>
      </div>
    </Rise>
  );
}

const PRE_REVENUE_PLACEHOLDER = 2000;

export default function ReceiptsScreen({ revenue, onNext, onBack }: ReceiptsScreenProps) {
  const isMobile = useIsMobile();
  const isPreRevenue = revenue === 0;
  const basis = isPreRevenue ? PRE_REVENUE_PLACEHOLDER : revenue;

  const oneTest = 12 * basis * LIFT;
  const threeTests = 12 * basis * (Math.pow(1 + LIFT, 3) - 1);

  const eyebrowCopy = isPreRevenue
    ? 'When you start earning'
    : "What you're leaving on the table";

  const headline: ReactNode = isPreRevenue ? (
    <>
      Most active creators land near {formatCurrency(PRE_REVENUE_PLACEHOLDER)}/mo. At that level,
      a typical winning test could be worth{' '}
      <span
        className="vara2-tabular"
        style={{
          color: 'var(--iris-11)',
          textShadow: '0 0 24px rgba(124, 124, 224, 0.45)',
        }}
      >
        {formatCurrency(oneTest)}
      </span>{' '}
      a year.
    </>
  ) : (
    <>
      At {formatCurrency(revenue)}/mo, a typical winning test could be worth{' '}
      <span
        className="vara2-tabular"
        style={{
          color: 'var(--iris-11)',
          textShadow: '0 0 24px rgba(124, 124, 224, 0.45)',
        }}
      >
        {formatCurrency(oneTest)}
      </span>{' '}
      a year.
    </>
  );

  const disclosureCopy = isPreRevenue
    ? `Based on a ${formatCurrency(PRE_REVENUE_PLACEHOLDER)}/mo example. A winning price test typically lifts revenue 4–7%. Not every test wins, and a result needs buyers reaching your checkout.`
    : 'A winning price test typically lifts revenue 4–7%. Not every test wins, and a result needs buyers reaching your checkout.';

  return (
    <div
      className="vara2-step-in"
      style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}
    >
      <div
        className="vara2-glow vara2-halo"
        style={{ top: -140, left: '50%', marginLeft: -180, animationDelay: '60ms' }}
      />

      <div className="vara2-onboarding-stack" style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <Rise delay={0}>
          <Text
            size="1"
            weight="medium"
            style={{
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--iris-11)',
            }}
          >
            {eyebrowCopy}
          </Text>
        </Rise>

        <Rise delay={100}>
          <Heading size={isMobile ? '6' : '8'}>{headline}</Heading>
        </Rise>

        <Rise delay={260}>
          <div
            style={{
              backgroundColor: 'var(--gray-a3)',
              border: '1px solid var(--gray-a5)',
              borderRadius: 'var(--radius-4)',
              padding: '0 var(--space-5)',
              marginTop: 'var(--space-3)',
            }}
          >
            <ReceiptRow
              title="One winning test"
              subtitle={`+5.5% on ${formatCurrency(basis)}/mo, for 12 months`}
              amount={oneTest}
              startDelayMs={320}
              rowDelay={320}
            />
            <ReceiptRow
              title="Three winning tests in a year"
              subtitle="Each win builds on the last"
              amount={threeTests}
              startDelayMs={500}
              rowDelay={500}
              amountColor="var(--iris-11)"
              isLast
            />
          </div>
        </Rise>

        <Rise delay={820}>
          <div
            style={{
              backgroundColor: 'var(--gray-a2)',
              border: '1px solid var(--gray-a5)',
              borderRadius: 'var(--radius-3)',
              padding: 'var(--space-3) var(--space-4)',
              display: 'flex',
              gap: 'var(--space-3)',
              alignItems: 'flex-start',
              marginTop: 'var(--space-2)',
            }}
          >
            <InfoOutline16 style={{ flexShrink: 0, marginTop: 2, color: 'var(--gray-10)' }} />
            <Text size="2" color="gray">
              {disclosureCopy}
            </Text>
          </div>
        </Rise>

        <Rise delay={920}>
          <div className="vara2-onboarding-action-row" style={{ display: 'flex', gap: 'var(--space-3)', paddingTop: 'var(--space-3)' }}>
            <Button
              variant="soft"
              color="gray"
              size="3"
              onClick={onBack}
              className="vara2-back-button"
              aria-label="Back"
              style={{ minHeight: 44 }}
            >
              ←<span className="vara2-back-label"> Back</span>
            </Button>
            <Button
              variant="solid"
              color="iris"
              size="3"
              onClick={onNext}
              className="vara2-cta"
              style={{ flex: 1, minHeight: 44 }}
            >
              Where are my buyers? →
            </Button>
          </div>
        </Rise>
      </div>
    </div>
  );
}
